import { useNavigate } from "react-router-dom";
import { Column, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomNetworkImage from "../../../common/components/image/CustomNetworkImage";
import { ShopStatus, StatusBadge } from "../../../common/components/table/CustomTable";
import { C2Text } from "../../../common/components/text/CustomText";
import CustomIcon from "../../../common/components/image/CustomIcon";
import DeviceActiveProgressBar from "../../../common/components/bar/DeviceActiveProgressBar";
import { useRef } from "react";
import { useClickOutside } from "../../../common/hooks/useClickOutside";
import DividerTextRow from "../../../common/components/text/DividerTextRow";
import { Theme } from "../../../common/Theme";
import { useDevice } from "../../../common/hooks/useDevice";

export default function MapMarkerTooltip({
    isOpen,
    onClose,
    shopCode,
    name,
    status,
    ownerName,
    contact,
    activeWasher,
    deactivatedWasher,
    activeDryer,
    deactivatedDryer,
    activeCombo,
    deactivatedCombo,
    images,
}) {
    const navigation = useNavigate();
    const currentDevice = useDevice();

    const tooltipRef = useRef(null);
    useClickOutside(tooltipRef, () => {
        if (isOpen) {
            onClose();
        }
    });

    if (!isOpen) {
        return null;
    }

    const displayImages = (images ?? []).slice(0, 5);
    const imgCount = displayImages.length;

    const renderImages = () => {
        if (imgCount === 0) return <SizedBox width={"100%"} height={"184px"} />;
        if (imgCount === 1) {
            return (
                <Row width={"100%"} height={"184px"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                    <CustomNetworkImage
                        src={displayImages[0].url}
                        altText={displayImages[0].alternativeText}
                        width={"395px"}
                        height={"184px"}
                    />
                </Row>
            );
        }
        if (imgCount === 2) {
            return (
                <Row width={"100%"} height={"184px"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                    <CustomNetworkImage
                        src={displayImages[0].url}
                        altText={displayImages[0].alternativeText}
                        width={"196px"}
                        height={"184px"}
                    />
                    <SizedBox width={"3px"} />
                    <CustomNetworkImage
                        src={displayImages[1].url}
                        altText={displayImages[1].alternativeText}
                        width={"196px"}
                        height={"184px"}
                    />
                </Row>
            );
        }
        if (imgCount === 3) {
            return (
                <Row width={"100%"} height={"184px"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                    <CustomNetworkImage
                        src={displayImages[0].url}
                        altText={displayImages[0].alternativeText}
                        width={"196px"}
                        height={"184px"}
                    />
                    <SizedBox width={"3px"} />
                    <Column justifyContent={"flex-start"} alignItem={"flex-start"}>
                        <CustomNetworkImage
                            src={displayImages[1].url}
                            altText={displayImages[1].alternativeText}
                            width={"196px"}
                            height={"91px"}
                        />
                        <SizedBox height={"3px"} />
                        <CustomNetworkImage
                            src={displayImages[2].url}
                            altText={displayImages[2].alternativeText}
                            width={"196px"}
                            height={"91px"}
                        />
                    </Column>
                </Row>
            );
        }
        return (
            <Row width={"100%"} height={"184px"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                <CustomNetworkImage
                    src={displayImages[0].url}
                    altText={displayImages[0].alternativeText}
                    width={"196px"}
                    height={"184px"}
                />
                <SizedBox width={"3px"} />
                <Column justifyContent={"flex-start"} alignItem={"flex-start"}>
                    <CustomNetworkImage
                        src={displayImages[1].url}
                        altText={displayImages[1].alternativeText}
                        width={"97px"}
                        height={"91px"}
                    />
                    <SizedBox height={"3px"} />
                    <CustomNetworkImage
                        src={displayImages[2].url}
                        altText={displayImages[2].alternativeText}
                        width={"97px"}
                        height={"91px"}
                    />
                </Column>
                <SizedBox width={"3px"} />
                <Column justifyContent={"flex-start"} alignItem={"flex-start"}>
                    <CustomNetworkImage
                        src={displayImages[3].url}
                        altText={displayImages[3].alternativeText}
                        width={"97px"}
                        height={"91px"}
                    />
                    {displayImages[4] && (
                        <>
                            <SizedBox height={"3px"} />
                            <CustomNetworkImage
                                src={displayImages[4].url}
                                altText={displayImages[4].alternativeText}
                                width={"97px"}
                                height={"91px"}
                            />
                        </>
                    )}
                </Column>
            </Row>
        );
    };

    let leftPositon = "215px";

    if (currentDevice === "mobile") {
        leftPositon = "-80px";
    } else if (currentDevice === "tablet") {
        leftPositon = "0";
    }

    return (
        <div
            style={{
                position: "absolute",
                left: leftPositon,
                top: currentDevice === "mobile" || currentDevice === "tablet" ? "120%" : "0",
                zIndex: 1000,
                boxShadow: `0px 4px 20px ${Theme.color.black_015}`,
                pointerEvents: "auto",
            }}
            ref={tooltipRef}
        >
            <SizedBox
                width={"395px"}
                height={"457px"}
                borderRadius={"20px"}
                backgroundColor={Theme.color.white}
                alignItem={"flex-start"}
                justifyContent={"flex-start"}
                style={{ position: "relative" }}
                onClick={() => navigation(`/shops/${shopCode}`)}
                cursor={"pointer"}
            >
                <div
                    style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        zIndex: 1001,
                        backgroundColor: Theme.color.white_05,
                        height: "34px",
                        width: "34px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "100%",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onClose) onClose();
                    }}
                >
                    <CustomIcon iconName={"close"} size={"24px"} cursor={"pointer"} />
                </div>
                {renderImages()}
                <SizedBox height={"12px"} />
                <Column padding={"0 20px"} width={"100%"} alignItem={"flex-start"} justifyContent={"flex-start"}>
                    <Row>
                        <ShopStatus status={"Open"} />
                        <SizedBox width={"6px"} />
                        <StatusBadge>{shopCode}</StatusBadge>
                    </Row>
                    <SizedBox height={"4px"} />
                    <C2Text fontWeight={"600"} fontColor={Theme.color.zinc850} lineHeight={"140%"}>
                        {name}
                    </C2Text>
                    <SizedBox height={"8px"} />
                    <DividerTextRow
                        items={[
                            { icon: "user", value: "Bow", iconGap: "6px" },
                            { icon: "phone", value: contact, iconGap: "6px" },
                        ]}
                    />
                    <SizedBox height={"16.5px"} />
                    <DeviceActiveProgressBar
                        type={"washer"}
                        total={activeWasher + deactivatedWasher}
                        active={activeWasher}
                    />
                    <DeviceActiveProgressBar
                        type={"dryer"}
                        total={activeDryer + deactivatedDryer}
                        active={activeDryer}
                    />
                    <DeviceActiveProgressBar
                        type={"combo"}
                        total={activeCombo + deactivatedCombo}
                        active={activeCombo}
                    />
                </Column>
            </SizedBox>
        </div>
    );
}
